import {
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
  Transition,
} from "@headlessui/react";
import {
  EllipsisVerticalIcon,
  MapPinIcon,
  CheckCircleIcon,
} from "@heroicons/react/20/solid";
import { Fragment } from "react";
import { DateTime } from "luxon";
import StatusIndicator from "../Shared/StatusIndicator";
import AppointmentTypeBadge from "../Shared/AppointmentTypeBadge";
import { APPOINTMENT_STATUS } from "../../config/appointment";
import { useUserPreferences } from "../../hooks/useUserPreferences";
import { getOriginalTimeDisplay } from "../../utility/timezoneUtils";

const TodayTimeline = ({
  appointments = [],
  isLoading,
  selectedDate,
  onView,
  onEdit,
  onCancel,
  onContact,
  onUpdateStatus,
  onClientClick,
}) => {
  const systemTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const { is12HourClock } = useUserPreferences();
  const now = DateTime.now();
  const viewingDate = selectedDate ? DateTime.fromJSDate(selectedDate) : DateTime.now();
  const isToday = viewingDate.hasSame(now, "day");

  const formatTime = (isoTime) => {
    if (!isoTime) return "";
    const format = is12HourClock ? "h:mm a" : "HH:mm";
    return DateTime.fromISO(isoTime).setZone(systemTimezone).toFormat(format);
  };

  const getFormattedAppointment = (appointment) => {
    const status = APPOINTMENT_STATUS.find((x) => x.id === appointment.status);
    return {
      ...appointment,
      id: appointment.id,
      formattedStart: formatTime(appointment.startTime),
      formattedEnd: formatTime(appointment.endTime),
      statusName: status?.name,
      statusColour: status?.colour,
      address: appointment.serviceAddress?.full,
    };
  };

  const getAppointmentState = (appointment) => {
    const start = DateTime.fromISO(appointment.startTime);
    const end = DateTime.fromISO(appointment.endTime);

    if (appointment.status === "cancelled") return "cancelled";
    if (appointment.status === "completed") return "completed";
    if (isToday && start <= now && end > now) return "current";
    if (isToday && end <= now) return "past";
    return "future";
  };

  const getOriginalTimeDisplayForAppointment = (appointment) => {
    if (
      !appointment.timeZoneId ||
      appointment.timeZoneId === systemTimezone ||
      !appointment.startTime
    ) {
      return null;
    }
    return getOriginalTimeDisplay(
      appointment.startTime,
      appointment.timeZoneId,
      is12HourClock
    );
  };

  const sortedAppointments = [...appointments].sort(
    (a, b) =>
      DateTime.fromISO(a.startTime).toMillis() -
      DateTime.fromISO(b.startTime).toMillis()
  );

  const scheduleTitle = isToday
    ? "Today's Schedule"
    : viewingDate.toFormat("cccc, MMM d");

  const sectionHeader = (
    <div>
      <p className="smallcaps text-ink-400 mb-1">Schedule</p>
      <div className="flex items-end gap-x-3">
        <h2 className="font-display text-2xl text-ink-900 tracking-display leading-none">
          {scheduleTitle}
        </h2>
        {sortedAppointments.length > 0 && (
          <span className="text-xs text-ink-500 tabular rounded-pill bg-cream-100 px-2 py-0.5 mb-1">
            {sortedAppointments.length}
          </span>
        )}
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <div className="bg-white rounded-card shadow-hairline border border-ink-100 p-6">
        <div className="space-y-5">
          <div className="space-y-2">
            <div className="h-3 w-20 bg-cream-200 rounded"></div>
            <div className="h-7 w-48 bg-cream-200 rounded"></div>
          </div>
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex gap-4">
                <div className="w-16 h-5 bg-cream-200 rounded"></div>
                <div className="flex-1 h-12 bg-cream-200 rounded-lg"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (sortedAppointments.length === 0) {
    // DashboardPage swaps in DashboardEmptyState for today's empty view, so this
    // component typically isn't mounted then. Returning null is a defensive fallback.
    if (isToday) {
      return null;
    }

    return (
      <div className="bg-white rounded-card shadow-hairline border border-ink-100 p-6">
        <div className="space-y-5">
          {sectionHeader}
          <div className="text-center py-10">
            <p className="text-ink-500">No appointments scheduled</p>
          </div>
        </div>
      </div>
    );
  }

  // Find the index where "now" sits — between the last past appointment and the
  // first future one — so we can render a Now divider when nothing is currently
  // happening. Skipped when a current appointment already anchors the eye.
  const hasCurrent = isToday && sortedAppointments.some(
    (a) => getAppointmentState(a) === "current"
  );
  const nowDividerIndex = (() => {
    if (!isToday || hasCurrent) return -1;
    const firstFutureIdx = sortedAppointments.findIndex((a) => {
      const start = DateTime.fromISO(a.startTime);
      return start > now && a.status !== "cancelled";
    });
    // Only render if there's something past AND something future.
    if (firstFutureIdx <= 0) return -1;
    return firstFutureIdx;
  })();

  const nowLabel = now.toFormat(is12HourClock ? "h:mm a" : "HH:mm");

  return (
    <div className="bg-white rounded-card shadow-hairline border border-ink-100 p-6">
      <div className="space-y-5">
        {sectionHeader}

        <div className="relative">
        {/* Timeline connector line — anchored to the dot column.
            Offset: time column w-16 (64px) + gap-4 (16px) + half-dot (5px) = 85px. */}
        <div className="absolute left-[85px] top-4 bottom-4 w-px bg-cream-300 hidden sm:block" aria-hidden="true"></div>

        <ul className="divide-y divide-cream-200">
          {sortedAppointments.map((appointment, idx) => {
            const formatted = getFormattedAppointment(appointment);
            const state = getAppointmentState(appointment);
            const originalTimeDisplay =
              getOriginalTimeDisplayForAppointment(appointment);
            const showEditOption = appointment.status !== "completed";
            const showCancelOption =
              appointment.status !== "cancelled" &&
              appointment.status !== "completed";

            const isCompleted = state === "completed";
            const isPast = state === "past" || isCompleted;
            const isCancelled = state === "cancelled";
            const isCurrent = state === "current";

            const rowDim = isCancelled
              ? "opacity-60"
              : isPast
                ? "opacity-70"
                : "";

            const metaParts = [];
            if (appointment.services && appointment.services.length > 0) {
              metaParts.push(appointment.services.map((s) => s.name).join(", "));
            }
            if (appointment.serviceAddress?.full) {
              metaParts.push(appointment.serviceAddress.full);
            }

            return (
              <Fragment key={appointment.id}>
                {idx === nowDividerIndex && (
                  <li className="py-3" aria-hidden="true">
                    <div className="flex items-center gap-3">
                      <span className="smallcaps text-accent-600">Now</span>
                      <span className="flex-1 h-px bg-accent-200"></span>
                      <span className="text-xs text-ink-400 tabular">
                        {nowLabel}
                      </span>
                    </div>
                  </li>
                )}
                <li
                  className={`flex gap-3 sm:gap-4 py-4 transition-colors ${rowDim}`}
                >
                  {/* Time column */}
                  <div className="flex-shrink-0 w-16 pt-0.5">
                    <div
                      className={`text-sm font-semibold tabular tracking-tight ${
                        isCurrent
                          ? "text-accent-600"
                          : isCancelled
                            ? "text-ink-400 line-through"
                            : "text-ink-900"
                      }`}
                    >
                      {formatted.formattedStart}
                    </div>
                    <div className="text-[11px] text-ink-400 tabular mt-0.5">
                      {formatted.formattedEnd}
                    </div>
                  </div>

                  {/* Timeline dot — sized to land on the rail. */}
                  <div className="flex-shrink-0 w-2.5 relative z-10 hidden sm:flex items-start justify-center pt-2">
                    {isCompleted ? (
                      <CheckCircleIcon className="size-3.5 text-signal-green-500 -mt-0.5 bg-white" />
                    ) : isCurrent ? (
                      <span className="size-2.5 rounded-full bg-accent-500 ring-4 ring-accent-100"></span>
                    ) : (
                      <span
                        className={`size-2 rounded-full ${
                          isCancelled ? "bg-ink-200" : "bg-ink-300"
                        }`}
                      ></span>
                    )}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0 flex items-start justify-between gap-x-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-x-2 min-w-0">
                        {isCompleted && (
                          <CheckCircleIcon
                            className="size-4 text-signal-green-500 flex-shrink-0 sm:hidden"
                            aria-label="Completed"
                          />
                        )}
                        <button
                          type="button"
                          onClick={() => onClientClick(appointment.clientId)}
                          disabled={!appointment.clientId}
                          className={`font-medium text-sm transition-colors duration-150 truncate ${
                            appointment.clientId
                              ? "hover:text-accent-500 hover:underline"
                              : "disabled:cursor-default"
                          } ${
                            isCancelled
                              ? "text-ink-500 line-through"
                              : "text-ink-900"
                          }`}
                        >
                          {appointment.clientFullName}
                        </button>
                      </div>

                      {/* Single muted meta line: services · location */}
                      {metaParts.length > 0 && (
                        <p className="text-xs text-ink-500 mt-1 truncate">
                          {metaParts.join(" · ")}
                        </p>
                      )}

                      {/* Original timezone display */}
                      {originalTimeDisplay && (
                        <p className="text-[11px] text-ink-400 mt-0.5">
                          {originalTimeDisplay}
                        </p>
                      )}
                    </div>

                    {/* Right-side chips + actions, off the name scan column */}
                    <div className="flex items-center gap-x-2 flex-shrink-0">
                      <div className="hidden sm:flex items-center gap-x-2">
                        <StatusIndicator
                          variant="dot"
                          hideWhenConfirmed
                          colour={formatted.statusColour}
                          label={formatted.statusName}
                        />
                        <AppointmentTypeBadge type={appointment.type} size="xs" />
                      </div>
                      {appointment.serviceAddress?.full && (
                        <a
                          href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                            appointment.serviceAddress.full
                          )}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          title={`Open ${appointment.serviceAddress.full} in Maps`}
                          className="hidden sm:inline-flex p-1.5 -m-1.5 text-ink-400 hover:text-accent-500 transition-colors"
                          aria-label="Open in Maps"
                        >
                          <MapPinIcon className="size-4" />
                        </a>
                      )}

                      {/* Actions menu */}
                    <Menu as="div" className="relative">
                      <MenuButton className="-m-2 p-2 text-ink-400 hover:text-ink-600">
                        <span className="sr-only">Open options</span>
                        <EllipsisVerticalIcon className="size-5" />
                      </MenuButton>

                      <Transition
                        as={Fragment}
                        enter="transition ease-out duration-100"
                        enterFrom="transform opacity-0 scale-95"
                        enterTo="transform opacity-100 scale-100"
                        leave="transition ease-in duration-75"
                        leaveFrom="transform opacity-100 scale-100"
                        leaveTo="transform opacity-0 scale-95"
                      >
                        <MenuItems
                          anchor="bottom end"
                          className="z-10 w-36 rounded-md bg-white py-2 shadow-lg ring-1 ring-ink-100 focus:outline-none"
                        >
                          <MenuItem>
                            {({ close }) => (
                              <button
                                type="button"
                                onClick={() => {
                                  const statusObj = APPOINTMENT_STATUS.find(
                                    (x) => x.id === appointment.status
                                  );
                                  onView({
                                    ...appointment,
                                    startTime: formatTime(
                                      appointment.startTime
                                    ),
                                    endTime: formatTime(appointment.endTime),
                                    status: statusObj?.name,
                                    statusColour: statusObj?.colour,
                                    address: appointment.serviceAddress?.full,
                                    clientName: appointment.clientFullName,
                                  });
                                  close();
                                }}
                                className="block w-full text-left px-3 py-1 text-sm/6 text-ink-900 data-[focus]:bg-cream-100 data-[focus]:outline-none"
                              >
                                View
                              </button>
                            )}
                          </MenuItem>
                          {showEditOption && (
                            <MenuItem>
                              {({ close }) => (
                                <button
                                  type="button"
                                  onClick={() => {
                                    onEdit(appointment);
                                    close();
                                  }}
                                  className="block w-full text-left px-3 py-1 text-sm/6 text-ink-900 data-[focus]:bg-cream-100 data-[focus]:outline-none"
                                >
                                  Edit
                                </button>
                              )}
                            </MenuItem>
                          )}
                          <MenuItem>
                            {({ close }) => (
                              <button
                                type="button"
                                onClick={() => {
                                  onContact(appointment);
                                  close();
                                }}
                                className="block w-full text-left px-3 py-1 text-sm/6 text-ink-900 data-[focus]:bg-cream-100 data-[focus]:outline-none whitespace-nowrap"
                              >
                                Contact Client
                              </button>
                            )}
                          </MenuItem>
                          {appointment.status === "pending" && (
                            <MenuItem>
                              {({ close }) => (
                                <button
                                  type="button"
                                  onClick={() => {
                                    onUpdateStatus(
                                      appointment.id,
                                      "confirmed"
                                    );
                                    close();
                                  }}
                                  className="block w-full text-left px-3 py-1 text-sm/6 text-ink-900 data-[focus]:bg-cream-100 data-[focus]:outline-none"
                                >
                                  Confirm
                                </button>
                              )}
                            </MenuItem>
                          )}
                          {appointment.status === "confirmed" && (
                            <MenuItem>
                              {({ close }) => (
                                <button
                                  type="button"
                                  onClick={() => {
                                    onUpdateStatus(
                                      appointment.id,
                                      "completed"
                                    );
                                    close();
                                  }}
                                  className="block w-full text-left px-3 py-1 text-sm/6 text-ink-900 data-[focus]:bg-cream-100 data-[focus]:outline-none"
                                >
                                  Complete
                                </button>
                              )}
                            </MenuItem>
                          )}
                          {showCancelOption && (
                            <MenuItem>
                              {({ close }) => (
                                <button
                                  type="button"
                                  onClick={() => {
                                    onCancel(appointment);
                                    close();
                                  }}
                                  className="block w-full text-left px-3 py-1 text-sm/6 text-signal-red-600 data-[focus]:bg-cream-100 data-[focus]:outline-none"
                                >
                                  Cancel
                                </button>
                              )}
                            </MenuItem>
                          )}
                        </MenuItems>
                      </Transition>
                    </Menu>
                    </div>
                  </div>
                </li>
              </Fragment>
            );
          })}
        </ul>
        </div>
      </div>
    </div>
  );
};

export default TodayTimeline;
